import React, { useState } from 'react';
import { Button, Typography, Row, Col, Space, Form, Input, FormModal } from 'components';
import { CheckCircleFilled, CloseCircleFilled, WarningFilled } from '@ant-design/icons';
import styles from './ItemStatusDisplay.module.scss';

const { Text } = Typography;

interface IdentifiedItem {
  id: string;
  sku: string;
  name?: string;
  matched: boolean;
}

interface UploadError {
  type: 'parse_error' | 'not_in_order';
  title: string;
  message?: string;
  sku?: string;
}

interface ItemStatusDisplayProps {
  isIdentifying: boolean;
  identifiedItem: IdentifiedItem | null;
  uploadError: UploadError | null;
  onRetake: () => void;
  onRaiseClaim: () => void;
  onWriteCode: (sku: string) => Promise<{ success: boolean; error?: string }>;
}

export const ItemStatusDisplay: React.FC<ItemStatusDisplayProps> = ({
  isIdentifying,
  identifiedItem,
  uploadError,
  onRetake,
  onRaiseClaim,
  onWriteCode,
}) => {
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenModal = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const result = await onWriteCode(values.sku);

      if (result.success) {
        handleCloseModal();
      } else {
        form.setFields([
          {
            name: 'sku',
            errors: [result.error || 'Something went wrong, please try again.'],
          },
        ]);
      }
    } catch {
      // Validation error, do nothing
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        {/* Identifying State */}
        {isIdentifying && (
          <Space direction="vertical" size={4}>
            <Text className={styles.statusText}>Identifying item...</Text>
            <Text className={styles.statusHint}>You can take additional photos while waiting</Text>
          </Space>
        )}

        {/* Success State - Item Identified */}
        {identifiedItem && !uploadError && (
          <Space direction="vertical" size={8}>
            <Space align="start" size={8}>
              <CheckCircleFilled className={styles.iconSuccess} />
              <Space direction="vertical" size={2}>
                <Text className={styles.titleSuccess}>Item Identified</Text>
                <Text className={styles.sku}>
                  SKU: <strong>{identifiedItem.sku}</strong>
                </Text>
              </Space>
            </Space>
          </Space>
        )}

        {/* Error State - Parse Error */}
        {uploadError && uploadError.type === 'parse_error' && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space align="start" size={8}>
              <CloseCircleFilled className={styles.iconError} />
              <Space direction="vertical" size={2}>
                <Text className={styles.titleError}>{uploadError.title}</Text>
                {uploadError.message && <Text className={styles.message}>{uploadError.message}</Text>}
              </Space>
            </Space>
            <Row gutter={8}>
              <Col span={12}>
                <Button className={styles.font14} type="default" size="middle" onClick={onRetake} block>
                  Retake
                </Button>
              </Col>
              <Col span={12}>
                <Button className={styles.font14} type="primary" size="middle" onClick={handleOpenModal} block>
                  Write Code
                </Button>
              </Col>
            </Row>
          </Space>
        )}

        {/* Error State - Not in Order */}
        {uploadError && uploadError.type === 'not_in_order' && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space align="start" size={8}>
              <WarningFilled className={styles.iconWarning} />
              <Space direction="vertical" size={2}>
                <Text className={styles.titleWarning}>{uploadError.title}</Text>
                {uploadError.sku && <Text className={styles.sku}>SKU: {uploadError.sku}</Text>}
              </Space>
            </Space>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Button className={styles.font14} type="default" size="middle" onClick={onRetake} block>
                  Retake
                </Button>
              </Col>
              <Col span={12}>
                <Button className={styles.font14} type="primary" size="middle" danger onClick={onRaiseClaim} block>
                  Raise Claim
                </Button>
              </Col>
              <Col span={24}>
                <Button className={styles.font14} type="primary" size="middle" onClick={handleOpenModal} block>
                  Write Code
                </Button>
              </Col>
            </Row>
          </Space>
        )}
      </div>

      {/* Manual SKU Input Modal */}
      <FormModal
        okText="Check"
        width={500}
        form={form}
        open={modalOpen}
        title="Enter Code Manually"
        isLoading={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={handleCloseModal}
      >
        <Form.Item label="SKU" name="sku" rules={[{ required: true, message: 'Please enter SKU' }]}>
          <Input placeholder="Enter Item SKU" size="large" autoFocus />
        </Form.Item>
      </FormModal>
    </>
  );
};
